/**
 * DroughtPanel — Sikhio drought index (current conditions, not a forecast).
 *
 * Renders the composite score plus the components it was actually built
 * from — a partial basis (e.g. dam feed down) is shown, never hidden.
 */

import type { DroughtBrief, FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";

interface Props {
  data: DroughtBrief | null;
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

const BAND_COLOR: Record<DroughtBrief["band"], string> = {
  normal:   "var(--good)",
  mild:     "var(--gold)",
  moderate: "var(--warn)",
  severe:   "var(--bad)",
};

const COMPONENT_LABEL: Record<string, string> = {
  reservoir:    "น้ำในเขื่อน",
  rain_deficit: "ฝนเทียบค่าปกติ",
  dry_spell:    "วันฝนไม่ตก",
};

export function DroughtPanel({ data, loading, ageMinutes, fallbackTier }: Props) {
  const d = data;

  if (loading && !d) {
    return (
      <div className="col">
        <div className="eyebrow">ภัยแล้ง / DROUGHT</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 12, marginTop: 6, width: "60%" }} />
      </div>
    );
  }

  if (!d) {
    return (
      <div className="col" style={{ gap: 4 }}>
        <PanelHeader
          title="ภัยแล้ง "
          fallbackTier={fallbackTier}
          ageMinutes={ageMinutes}
          source="dam.rain.climatology"
        />
        <div className="caption mono" style={{ color: "var(--warn)" }} role="status">
          ◆ FEED GAP · drought index unavailable
        </div>
      </div>
    );
  }

  const col = BAND_COLOR[d.band] ?? "var(--data)";

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="ภัยแล้ง "
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
        source="dam.rain.climatology"
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span className="mono" style={{ fontSize: "var(--size-h3)", color: col, fontWeight: 700 }}>
          {d.score}
        </span>
        <span className="eyebrow" style={{ color: col }}>
          {d.bandTh}
        </span>
      </div>
      <div style={{ height: 5, background: "var(--line)", position: "relative", marginTop: 4 }}>
        <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${d.score}%`, background: col }} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "4px 12px", marginTop: 6 }}>
        {d.components.map((c) => (
          <div key={c.key} style={{ display: "contents" }}>
            <span className="caption">{COMPONENT_LABEL[c.key] ?? c.key}</span>
            <span
              className="mono caption"
              style={{ textAlign: "right", color: c.value === null ? "var(--text-3)" : undefined }}
            >
              {c.value === null ? "ไม่มีข้อมูล" : c.value}
            </span>
          </div>
        ))}
        {d.rainAnomalyPct !== null && (
          <>
            <span className="caption">ฝน 30 วัน</span>
            <span
              className="mono caption"
              style={{ textAlign: "right", color: d.rainAnomalyPct < -30 ? "var(--warn)" : undefined }}
            >
              {d.rain30dMm?.toFixed(0)} มม. ({d.rainAnomalyPct > 0 ? "+" : ""}{d.rainAnomalyPct}%)
            </span>
          </>
        )}
        {d.daysSinceRain !== null && (
          <>
            <span className="caption">ฝนตกล่าสุด</span>
            <span className="mono caption" style={{ textAlign: "right" }}>
              {d.daysSinceRain === 0 ? "วันนี้" : `${d.daysSinceRain} วันก่อน`}
            </span>
          </>
        )}
      </div>

      {d.basis.length < 3 && (
        <div className="caption" style={{ marginTop: 4, color: "var(--warn)" }}>
          คำนวณจาก {d.basis.map((b) => COMPONENT_LABEL[b] ?? b).join(" + ")} เท่านั้น
        </div>
      )}
      <div className="caption" style={{ marginTop: 4, color: "var(--text-3)" }}>
        สภาพปัจจุบันจากข้อมูลจริง — ไม่ใช่การพยากรณ์
      </div>
    </div>
  );
}
